/**
 * Anonymous alias: profile, devices, E2E, privacy, appearance, etc.
 */
export const identity = {
    title: 'Anonymous Alias',
    notLoggedIn: 'Not logged in',
    loginButton: 'Login to Alias',
    loginToExistingButton: 'Login to Existing Alias',
    createAliasButton: 'Create Alias',
    createButton: 'Create Alias',
    /** Aria labels for password show/hide controls (login, unlock, etc.) */
    passwordVisibility: {
      show: 'Show password',
      hide: 'Hide password',
    },
    logoutButton: 'Logout',
    loggedInAs: 'Logged in as',
    // Menu items for identity flyout
    menu: {
      profile: 'Profile',
      privacy: 'Privacy & Security',
      appearance: 'Appearance',
      notifications: 'Notifications',
      audioVideo: 'Audio & Video',
      ciphers: 'Ciphers',
      devices: 'Devices',
      emojis: 'Custom Emojis',
      subscription: 'Subscription',
    },
    // Actions for identity interactions
    actions: {
      viewProfile: 'View Profile',
    },
    // Device management
    device: {
      newDeviceTitle: 'New Device Added',
      newDeviceMessage: 'You\'re now logged in on {{deviceName}}. Click here to manage your devices.',
    },
    // Moderation suspension
    suspension: {
      bannedTitle: 'Alias Permanently Banned',
      suspendedTitle: 'Alias Suspended',
      reason: 'Reason',
      reportId: 'Report ID',
      suspendedUntil: 'Suspended until',
      appealMessage: 'If you believe this was made in error, you may appeal by emailing',
      appealEmail: 'disputes@adieuu.com',
      appealInstructions: 'with your report ID.',
      dismiss: 'I understand',
    },
    // Devices page
    devices: {
      title: 'Devices',
      subtitle: 'Manage devices that have access to your alias. Each device has its own encryption keys.',
      tabs: {
        devices: 'Devices',
        sessions: 'Sessions',
        activity: 'Activity',
      },
      yourDevices: 'Your Devices',
      noDevices: 'No devices found.',
      thisDevice: 'This device',
      lastActive: 'Active',
      added: 'Added',
      rename: 'Rename',
      remove: 'Delete',
      removeAllOthers: 'Delete all other devices',
      // Backup export/import
      exportKeyBackup: 'Export Backup',
      importKeyBackup: 'Import Backup',
      export: {
        title: 'Export Backup',
        description: 'Create an encrypted backup of your alias data. You will need the export password to restore this backup later.',
        includeLabel: 'Include in backup',
        contentDevices: 'Device Keys',
        contentCiphers: 'Ciphers',
        passwordLabel: 'Export Password',
        passwordPlaceholder: 'Choose a strong password',
        confirmPasswordLabel: 'Confirm Password',
        confirmPasswordPlaceholder: 'Confirm your password',
        warning: 'If you forget this password, the backup cannot be recovered. Store it in a safe place (password manager, encrypted drive, etc.).',
        submit: 'Export Backup',
        exporting: 'Encrypting and exporting...',
        success: 'Backup exported successfully.',
        errorPasswordMismatch: 'Passwords do not match.',
        errorPasswordTooShort: 'Password must be at least {{min}} characters.',
        errorNothingSelected: 'Select at least one data type to export.',
        errorNoData: 'No data found to export for this alias.',
        errorFailed: 'Failed to export backup.',
      },
      import: {
        title: 'Import Backup',
        description: 'Restore data from a previously exported backup file.',
        pickFile: 'Choose Backup File',
        pickFileHint: 'Select a .adieuu-keys file',
        passwordLabel: 'Export Password',
        passwordPlaceholder: 'Enter the export password',
        passwordNeeded: 'Your alias password is needed to re-encrypt the imported data for this device.',
        passphraseLabel: 'Alias Password',
        passphrasePlaceholder: 'Enter your alias password',
        submit: 'Decrypt & Import',
        importing: 'Decrypting and importing...',
        summaryDevices: '{{count}} device key(s)',
        summaryCiphers: '{{count}} cipher(s)',
        summaryContains: 'Backup contains: {{items}}',
        mergeTitle: 'Existing Data Found',
        mergeDescription: '{{count}} of {{total}} items in this backup already exist on this device.',
        mergeSkip: 'Skip existing',
        mergeReplace: 'Replace existing',
        identityMismatch: 'This backup is for a different alias. Sign in to the correct alias and try again.',
        errorWrongPassword: 'Incorrect export password. The backup could not be decrypted.',
        errorCorruptFile: 'The backup file is damaged or not a valid Adieuu backup.',
        errorUnsupportedVersion: 'This backup was created with a newer version of Adieuu. Please update.',
        errorNoData: 'The backup file contains no data.',
        errorFailed: 'Failed to import backup.',
      },
      forwardSecrecy: {
        title: 'Forward Secrecy',
        betaBadge: 'Advanced',
        betaWarning: "If you usually want mid-long term history in your conversations, you should not enable Forward Secrecy as default. You can always enable it on-demand for individual messages by toggling on the 'FS' icon in your message composer. The settings below apply to such messages.",
        description: "End-to-end encryption (E2EE) ensures only you and your recipients devices can read the messages, but what happens if your device is stolen? An attacker who can get into your phone can then read your messages. This is what Forward Secrecy aims to address: it swaps your keys more aggressively, meaning someone in the future (even you) can't read your old messages.",
        enabledTitle: 'Enable Forward Secrecy by default',
        enabledHint: 'When enabled, all new messages use forward secrecy unless overridden per-conversation or per-message. Messages sent without forward secrecy remain end-to-end encrypted but persist in history.',
        enabledUpdated: 'Forward secrecy default updated.',
        securityLevelTitle: 'Security Level',
        deletionPolicyTitle: 'Signed Pre-Key Deletion Policy',
        manualRotationTitle: 'Manual Rotation',
        rotateNow: 'Rotate Keys Now',
        rotateSuccess: 'Signed pre-key rotated successfully.',
        rotateErrorTitle: 'Rotation failed',
        rotateErrorBody: 'Unable to rotate signed pre-key right now.',
        securityUpdated: 'Security level updated.',
        deletionUpdated: 'Deletion policy updated.',
        lastRotatedAt: 'Last rotated: {{date}}',
        lastRotatedUnknown: 'No manual rotation this session.',
        security: {
          very_lax: {
            title: 'Very Lax',
            description: 'Rotate every 30 days. Longest message history window.',
          },
          lax: {
            title: 'Lax',
            description: 'Rotate every 2 weeks.',
          },
          standard: {
            title: 'Standard',
            description: 'Rotate every 7 days.',
          },
          medium: {
            title: 'Medium',
            description: 'Rotate every 24 hours.',
          },
          high: {
            title: 'High',
            description: 'Rotate every 4 hours.',
          },
          maximum: {
            title: 'Maximum',
            description: 'Rotate every hour. Tightest forward secrecy.',
          },
        },
        deletion: {
          afterSync: {
            title: 'After Sync (best for convenience)',
            description: 'Keeps retired keys longer to reduce message loss risk.',
          },
          timed: {
            title: 'Timed',
            description: 'Deletes retired keys on a strict timer for tighter secrecy.',
          },
          immediate: {
            title: 'Immediate (best for security)',
            description: 'Deletes retired keys immediately on rotation. Old FS messages become permanently unreadable.',
          },
          immediateConfirmTitle: 'Enable immediate deletion?',
          immediateConfirmBody: 'With immediate deletion, retired encryption keys are permanently deleted every time keys are rotated. Forward-secrecy messages encrypted under old keys will become unreadable unless locally cached. This cannot be undone.',
          immediateConfirmAction: 'Enable immediate deletion',
        },
        clearCacheOnRotation: 'Also clear local message cache when keys are deleted',
        clearCacheOnRotationHint: 'When enabled, the local FS message cache is cleared alongside key deletion, making old messages fully unreadable. When disabled, decrypted copies of forward-secrecy messages stay cached on this device after keys are deleted — anyone with access to this device could still read them, which weakens forward secrecy locally.',
        clearCacheConfirmTitle: 'Enable cache clearing on rotation?',
        clearCacheConfirmBody: 'When keys are deleted (by policy or manually), the locally cached decrypted messages will also be removed. Old forward-secrecy messages will become permanently unreadable, with no fallback.',
        clearCacheConfirmAction: 'Enable cache clearing',
        clearCacheUpdated: 'Cache clearing setting updated.',
        purgeTitle: 'Purge Retired Keys',
        purgeDescription: 'Permanently delete all retired pre-key private keys on this device. FS messages encrypted under those keys will become unreadable unless locally cached.',
        purgeButton: 'Purge Retired Keys',
        purgeConfirmTitle: 'Purge all retired keys?',
        purgeConfirmBody: 'This will permanently delete all retired signed pre-key private keys from this device. Any forward-secrecy messages encrypted under those keys will become unreadable.',
        purgeConfirmClearCache: 'Also clear local message cache',
        purgeConfirmAction: 'Purge keys',
        purgeSuccess: 'Purged {{count}} retired key(s).',
        purgeNone: 'No retired keys to purge.',
        purgeErrorTitle: 'Purge failed',
        purgeErrorBody: 'Unable to purge retired keys right now.',
      },
    },
    // Activity tracking
    activity: {
      title: 'Device Activity',
      description: 'Control whether this device reports its last-active time. This is only visible to you on your other devices -- no one else can see it.',
      whenActive: 'When active',
      whenActiveDesc: 'Only update when you interact with the app',
      periodic: 'Periodic',
      periodicDesc: 'Update at regular intervals while the app is open',
      disabled: 'Disabled',
      disabledDesc: "This device won't report when it was last used",
      updateInterval: 'Update interval',
      interval15: 'Every 15 minutes',
      interval30: 'Every 30 minutes',
      interval60: 'Every hour',
      settingUpdated: 'Activity tracking preference updated',
    },
    // Identity session management
    sessions: {
      title: 'Alias Sessions',
      description: 'These are the active sessions for your alias. You can revoke access to any session you don\'t recognize.',
      currentSession: 'Current session',
      lastActive: 'Last active',
      created: 'Created',
      revokeSession: 'Revoke',
      revokeAllOthers: 'Revoke all other sessions',
      revokeAllConfirmTitle: 'Revoke all other sessions?',
      revokeAllConfirmDescription: 'This will sign out all other sessions for this alias. You will remain signed in on this device.',
      noOtherSessions: 'No other active sessions.',
      sessionRevoked: 'Session revoked successfully.',
      allSessionsRevoked: '{{count}} session(s) revoked successfully.',
      unknownDevice: 'Unknown device',
    },
    // Create modal
    create: {
      title: 'Create Anonymous Alias',
      subtitle: 'Your alias is who you are on Adieuu. Your messages, activity, etc are attached to your Alias, NOT your account, through cryptography and our unique architecture.',
      passwordPlaceholder: 'Enter a memorable password',
      passwordConfirmPlaceholder: 'Confirm your password',
      passwordHint: 'Use a sentence or phrase (8+ characters). You will need this to login.',
      passwordMismatch: 'Passwords do not match.',
      passwordExamplesTitle: 'Strong Password Examples',
      passwordExamples: [
        'the purple elephant dances at midnight',
        'my coffee is always too hot on tuesdays',
        'three blind mice ran up the clock tower',
        'sunshine through the kitchen window 2024',
      ],
      passwordExamplesTip: 'Sentences and phrases are easier to remember and harder to crack than random characters.',
      passwordExamplesLink: 'Need examples?',
      // Password strength hints
      passwordStrength: {
        match: 'Passwords match',
        /** Shown when both fields agree but the password is still under the minimum. */
        tooShort: 'Must be at least {{min}} chars',
        weak: "We'll accept this, but this is a very weak password.",
        medium: "Reasonable length, but it's 2026: longer is better.",
        strong: "Very length. Much defend. Wow.",
        veryStrong: "He protec, he attacc, much fortress, no crack.",
      },
      // Wizard step titles
      stepUsername: 'Username',
      stepDisplayName: 'Display name',
      stepPassword: 'Password',
      usernameLabel: 'Username',
      usernamePlaceholder: 'Choose a username',
      /** Shown to both tiers: the rule that holds regardless of who is choosing. */
      usernameHintUnique: 'Usernames are unique across Adieuu, and always lowercase.',
      /** Paid aliases pick their own name. */
      usernameNotePaid:
        'Your plan lets you choose any username that is still available. Letters, numbers, hyphens, and underscores.',
      /** Free aliases get a generated name they can reroll, but cannot type one. */
      usernameNoteFree:
        'Free accounts pick from generated usernames (to reduce squatting). Reroll as many times as you like, or upgrade to a paid plan to choose your own. Paid accounts also gain access to multiple aliases and other features.',
      usernameRotate: 'Generate a different username',
      displayNameLabel: 'Display name',
      displayNamePlaceholder: 'Display name',
      displayNameHint:
        'How you appear on your profile and in conversations. Your username is also visible.',
      displayNamePrivacyLabel: 'Display Name Visibility',
      displayNamePrivacyHintPublic: 'Everyone can see your display name.',
      displayNamePrivacyHintFriends: 'Friends see {{displayName}}, others see @{{username}}.',
      displayNamePrivacyHintHidden: 'Everyone else sees @{{username}} instead.',
      submitButton: 'Create Alias',
      creating: 'Creating...',
      creatingTitle: 'Creating Your Alias',
      creatingSubtitle: 'Setting up encryption keys. This may take a moment...',
      success: 'Alias created successfully!',
      redirecting: 'Redirecting to login...',
      errorUsernameTaken: 'This username is already taken.',
      errorUsernameBlocked: 'That username is not available. Please choose another.',
      errorUsernameTooShort: 'Usernames must be at least 3 characters.',
      errorUsernameTooLong: 'Usernames must be 30 characters or fewer.',
      errorUsernameInvalidChars: 'Use lowercase letters, numbers, hyphens, and underscores only.',
      /** Free tier tried to submit a name the generator did not produce. */
      errorUsernameNotGenerated: 'Please use one of the generated usernames.',
      errorDisplayNameBlocked: 'That display name is not available. Please choose another.',
      errorDisplayNameTooShort: 'Please enter a display name.',
      errorDisplayNameTooLong: 'Display names must be 50 characters or fewer.',
      /** The check could not run at all, as opposed to the name being rejected. */
      errorCheckFailed: "Couldn't check that right now. Please try again.",
      errorMaxIdentities: 'You have reached the maximum number of aliases.',
      errorPassphraseExists: 'An alias with this password already exists. Each alias needs its own password.',
      errorValidation: 'Please check your input and try again.',
      errorPayloadTooLarge: 'Encryption setup is too large to upload. Please try again.',
      noRecoveryWarning: 'Store your password in a safe place. Without it, you cannot decrypt your alias on new devices.',
    },
    // Login modal
    login: {
      title: 'Login to Alias',
      subtitle: 'Enter your password to access your anonymous alias.',
      passwordPlaceholder: 'Enter your password',
      submitButton: 'Login',
      loggingIn: 'Logging in...',
      loggingInTitle: 'Logging In',
      redirecting: 'Opening your alias...',
      success: 'Logged in successfully!',
      errorInvalid: 'Invalid password. Please try again.',
      errorLocked: 'Too many failed attempts. Please try again later.',
      errorRateLimited: 'Please wait {{seconds}} seconds before trying again.',
      errorSession: 'Your account session expired. Please sign in again.',
      attemptsRemaining: '{{remaining}} attempts remaining before lockout.',
      noIdentity: "You don't have an alias yet.",
      createPrompt: 'Create one to get started.',
      /** Shown on login when user already has at least one alias but may create another */
      createAnotherPrompt: 'Create a new alias',
      changePasswordLink: 'Change alias password',
      // Login status messages
      status: {
        authenticating: 'Verifying password...',
        deriving_keys: 'Deriving encryption keys...',
        loading_device: 'Loading device keys...',
        decrypting_bundle: 'Decrypting signing keys...',
        web_device_choice: 'Waiting for device choice...',
        generating_keys: 'Generating device encryption keys...',
        registering_device: 'Registering device...',
        complete: 'Complete!',
      },
    },
    // Unlock modal (after page refresh)
    unlock: {
      title: 'Session Locked',
      subtitle: 'Enter your password to unlock your session.',
      passwordPlaceholder: 'Enter your password',
      passwordRequired: 'Password must be at least 8 characters.',
      submitButton: 'Unlock',
      minimize: 'Minimize',
      success: 'Unlocked!',
      errorInvalid: 'Invalid password. Please try again.',
      logoutButton: 'Fully Logout',
      logoutAndExitButton: 'Logout and exit app',
      panicButton: 'Panic: wipe local data…',
      panicConfirmTitle: 'Erase local data?',
      panicConfirmBody:
        'This will sign you out on the server, delete keys, cached data, and other locally stored information for this app on this device, then try to quit. Your account on Adieuu is not deleted. Installed application files may remain.',
      panicConfirmAction: 'Wipe local data and quit',
    },
    // Delete confirmation
    delete: {
      title: 'Delete Alias',
      warning: 'This action cannot be undone. Your alias will be permanently deleted.',
      confirmButton: 'Delete Alias',
    },

    // E2E Encryption
    e2e: {
      // Key generation and initialization
      initializingKeys: 'Setting up encryption keys...',
      keyGenerationFailed: 'Failed to generate encryption keys. Please try again.',
      bundleEncryptionFailed: 'Failed to encrypt signing key bundle.',
      bundleUploadFailed: 'Failed to upload encryption keys to server.',
      deviceRegistrationFailed: 'Failed to register this device for encryption.',

      // Bundle operations
      bundleFetchFailed: 'Failed to fetch encryption keys from server.',
      bundleDecryptFailed: 'Failed to decrypt encryption keys. Check your password.',
      invalidBundleData: 'Invalid encryption key data received from server.',

      // Device key storage
      deviceKeyStorageFailed: 'Failed to store device encryption keys.',
      deviceKeyLoadFailed: 'Failed to load device encryption keys.',
      deviceKeyNotFound: 'No encryption keys found for this device.',

      // Key storage warnings (banner)
      keyStorageWarning: {
        teeUnavailable: 'Your encryption keys are not protected by the OS keychain. They are still encrypted with your password, but OS-level protection is unavailable. Ensure your system keyring (KWallet, GNOME Keyring, etc.) is running.',
        teeFailed: 'The OS keychain encountered an error while protecting your encryption keys. Keys have been saved with password encryption only. Error: {{error}}',
        dismiss: 'Dismiss',
      },

      // App update notification (banner)
      updateBanner: {
        message: 'A new version of Adieuu is available.',
        messageVersion: 'Adieuu {{version}} is available.',
        download: 'Download',
        refresh: 'Refresh',
        restart: 'Restart Now',
        viewDetails: 'View Update Details',
        available: 'Update Available',
        later: 'Later',
        downloading: 'Downloading update...',
        downloadingProgress: 'Downloading update... {{percent}}%',
        downloadingBytes: '{{transferred}} / {{total}}',
        ready: 'Update ready -- restart to apply.',
        installing: 'Installing update...',
        installingVersion: 'Installing Adieuu {{version}}...',
        pleaseWait: 'Please wait while the update is installed. The app will restart automatically.',
        minimize: 'Minimize',
        retryUpdate: 'Retry',
        errorMessage: 'Update failed: {{message}}',
        errorGeneric: 'Could not complete the update. Please try again.',
      },

      // Login flow
      newDeviceDetected: 'New device detected. Setting up encryption...',
      existingDeviceLoaded: 'Encryption keys loaded successfully.',
      signingKeyDecrypted: 'Signing key decrypted successfully.',

      // Separate password
      separatePassword: {
        label: 'Use separate password for encryption keys',
        hint: 'Advanced: Protect your encryption keys with a different password than your alias login.',
        placeholder: 'Enter encryption password',
        confirmPlaceholder: 'Confirm encryption password',
        mismatch: 'Encryption passwords do not match.',
        tooShort: 'Encryption password must be at least 12 characters.',
        sameAsIdentity: 'Encryption password must be different from alias password.',
        required: 'Both passwords are required to access messages on new devices.',
      },

      // Device naming
      deviceName: {
        prompt: 'Name this device',
        placeholder: 'e.g., Work Laptop, iPhone',
        hint: 'This name helps you identify devices in your account.',
        defaultDesktop: 'Desktop',
        defaultMobile: 'Mobile',
        defaultWeb: 'Web Browser',
        defaultUnknown: 'Unknown Device',
      },

      // Web device choice modal
      webDeviceChoice: {
        title: 'How should this browser store encryption keys?',
        sharedTitle: 'Shared Web Device (more user-friendly)',
        sharedDescription: 'Keys are locally encrypted, then stored on the server and recovered automatically after cache clears. Shared across other browser sessions using this device option.',
        individualTitle: 'Individual Device (more secure)',
        individualDescription: 'Keys are stored in this browser only. If your browser cache is cleared, these keys will be lost and a new device will be created. This is safer, but less convenient.',
        confirm: 'Continue',
      },

      // Remote passphrase-change migration prompt (shown on other devices)
      migrationPrompt: {
        title: 'Restore your message history',
        description:
          'Your alias password was changed on another device. Enter your previous password to keep messages on this device readable. Your keys stay encrypted on this device the whole time.',
        oldPasswordLabel: 'Previous alias password',
        migrate: 'Restore history',
        processing: 'Restoring...',
        optOut: 'Set up with new keys instead',
        optOutWarning:
          'If you continue without your previous password, messages already encrypted for this device will no longer be readable here.',
        errorWrongPassword: 'That password did not match. Please try again.',
        errorFailed: 'Could not restore your keys. Please try again or set up with new keys.',
      },

      // Shared web device revocation
      webDeviceRevocation: {
        label: 'Web (Shared Web Device)',
        subtitle: 'Shared by browser sessions where you enabled the "Shared Web Device" option',
        confirmTitle: 'Revoke shared web device?',
        confirmBody: 'This will sign out all web browser sessions using this device. Messages encrypted for this device will no longer be readable from any browser. You can re-enroll a new shared web device on your next web login.',
        cancel: 'Cancel',
        confirm: 'Revoke',
      },

      // Errors
      errors: {
        webCryptoUnavailable: 'Web Crypto API is not available. Please use a modern browser.',
        indexedDbUnavailable: 'IndexedDB is not available. Private browsing mode may not be supported.',
        kemEncryptionFailed: 'Failed to encrypt post-quantum keys.',
        kemDecryptionFailed: 'Failed to decrypt post-quantum keys. Check your password.',
        invalidKeySize: 'Invalid key size. Key data may be corrupted.',
        argon2Failed: 'Key derivation failed. Please try again.',
      },
    },
    // Profile page
    profile: {
      title: 'Profile',
      subtitle: 'View and manage your alias profile. Hover over elements to edit them.',
      displayName: 'Display Name',
      displayNamePlaceholder: 'Enter a display name',
      bio: 'Bio',
      bioPlaceholder: 'Tell people a bit about yourself',
      bioCharCount: '{{count}}/{{max}}',
      avatar: 'Avatar',
      banner: 'Banner',
      profileColors: 'Profile Colours',
      achievements: 'Achievements',
      friends: 'Friends',
      colorAccent: 'Accent',
      colorCardBackground: 'Card BG',
      colorBackground: 'Background',
      privacySettings: 'Privacy Settings',
      privacyLabel: '{{field}} visibility',
      privacyPublic: 'Public',
      privacyFriends: 'Friends',
      privacyPrivate: 'Only me',
      save: 'Save changes',
      saving: 'Saving...',
      saved: 'Profile updated.',
      uploadAvatar: 'Upload avatar',
      uploadBanner: 'Upload banner',
      removeAvatar: 'Remove avatar',
      removeBanner: 'Remove banner',
      changeAvatar: 'Change avatar',
      changeBanner: 'Change banner',
      uploadHint: 'Click or drag to upload',
      uploadProcessing: 'Processing...',
      uploadError: 'Upload failed: {{error}}',
      preview: 'Preview',
      previewAs: 'Preview as',
      previewPublic: 'Public',
      previewFriend: 'Friend',
      previewSelf: 'You (Editing)',
      noChanges: 'No changes to save.',
      unsavedChanges: 'You have unsaved profile changes.',
      discardChanges: 'Discard changes',
      achievementsHiddenPreview:
        'Achievements are hidden from this audience based on your privacy settings.',
      badges: 'Badges',
      badgesSelector: 'Select Badges',
      badgesMaxReached: 'You can display up to 3 badges.',
    },
    // Public profile view (read-only)
    profileView: {
      notFound: 'This alias could not be found.',
      error: 'Something went wrong loading this profile. Please try again.',
      editProfile: 'Edit profile',
      sendDm: 'Send DM',
      lastActive: 'Last active {{date}}',
      joined: 'Joined {{date}}',
      tabAbout: 'About',
      tabPosts: 'Posts',
      tabSpaces: 'Spaces',
      tabAchievements: 'Achievements',
      tabReports: 'Reports',
      tabFriends: 'Friends',
      tabFriendsCount: 'Friends ({{count}})',
      postsPlaceholder: 'Available in the future when we add feeds and posts!',
      spacesPlaceholder:
        'Public & Mutual Spaces this user is a member of will be visible here when we add Spaces in the near future!',
      reportsPlaceholder: 'Not yet available, coming soon.',
      friendsHidden: 'This user\'s friends list is private.',
      friendsEmpty: 'No friends to display.',
      friendsLoading: 'Loading friends...',
      friendsSearch: 'Search friends...',
      friendsSearchLabel: 'Search friends',
      friendsNoResults: 'No friends match your search.',
    },
    // Privacy page (identity-scoped)
    privacy: {
      title: 'Privacy & Security',
      subtitle: 'Manage your privacy settings and security options.',
      tabs: {
        general: 'General',
        forwardSecrecy: 'Forward Secrecy',
        changePassword: 'Alias Password',
        performance: 'Performance',
      },
      mediaCache: {
        title: 'Media cache',
        description:
          'Decrypted images and videos are kept in memory so they do not download again as you scroll. Nothing is written to disk, and the cache empties when the app closes.',
        usage_one: '{{used}} of {{max}} used · {{count}} item',
        usage_other: '{{used}} of {{max}} used · {{count}} items',
        inUse: '{{size}} is on screen right now and stays until you navigate away from it.',
        clear: 'Clear media cache',
        cleared: 'Cleared {{size}} of cached media.',
      },
      memory: {
        title: 'Memory usage',
        descriptionDesktop:
          'How much RAM each part of the app is using right now, largest first. Figures refresh every few seconds while this tab is open.',
        ram: '{{size}} RAM',
        explain: {
          appWindow:
            'Everything you see and interact with: the interface, the messages loaded in the open conversation or channel, and the images on screen. It usually uses the most RAM, rises as you scroll back through history or open media, and settles again when you move on.',
          main:
            'Orchestrates the components of the app: windows, notifications, the tray icon, updates, and secure storage. It also holds decrypted images and videos kept for fast scrolling, which you can clear under Media cache below.',
          gpu: 'Draws the interface on screen and decodes video. It grows with larger or high-resolution windows, animations, and video calls or screen shares.',
          network:
            'The network service, which manages connections to servers, DNS lookups, and the download cache. This is the RAM it uses, not how much data the app sends or receives, and should generally stay fairly.',
          audio: 'Plays and records sound: notification sounds and call audio. Chromium starts it the first time sound is needed.',
          videoCapture:
            'Reads your camera or screen during video calls and screen sharing. Chromium starts it when a camera or screen capture begins.',
          utility: 'Small helper processes Chromium runs for storage, decoding, and other background work.',
          window: 'A secondary window, such as the screen share picker or age verification. Its RAM is released when the window closes.',
          other: 'Chromium support processes used to start and sandbox the others. They use little RAM.',
        },
        descriptionBrowser:
          'Browsers only let a page see a rough figure for its own JavaScript memory. The desktop app shows a full breakdown.',
        measuring: 'Measuring…',
        error: 'Could not read memory usage.',
        browserHeap: 'JavaScript memory: {{size}}',
        browserUnsupported: 'This browser does not report memory usage.',
        appWindowDetail: 'JavaScript {{js}} · page content {{page}}',
        mainDetail: 'Includes {{size}} of cached media',
        processCount_one: '{{count}} process',
        processCount_other: '{{count}} processes',
        kinds: {
          main: 'Main Process',
          appWindow: 'App UI/X',
          window: 'Other window',
          windowNamed: 'Window: {{name}}',
          gpu: 'Graphics (GPU)',
          network: 'Network',
          audio: 'Audio',
          videoCapture: 'Camera and screen capture',
          utility: 'Background services',
          other: 'Other',
        },
      },
      blockedUsers: 'Blocked Users',
      profileVisibility: 'Profile Visibility',
      readReceipts: 'Read Receipts',
      typingIndicators: 'Typing Indicators',
      lastSeen: 'Last Seen',
      conversationsTitle: 'Conversations',
      conversationsDescription: 'Control how others can add you to group conversations.',
      groupApprovalTitle: 'Require group approval',
      groupApprovalHint: 'When enabled, you must approve before being added to group conversations. You will receive an invitation you can accept or decline.',
      groupApprovalEnabled: 'Group approval is now required.',
      groupApprovalDisabled: 'Group approval is no longer required.',
      groupApprovalError: 'Failed to update setting. Please try again.',
      deviceTrustTitle: 'Device trust',
      deviceTrustDescription:
        'Choose whether this alias pauses sending when someone (including you) adds or changes a device.',
      deviceTrustPauseTitle: 'Pause sending and calling until I accept new devices',
      deviceTrustPauseHint:
        'When off, you will see a warning you can dismiss and can keep messaging. When on, this device will not encrypt messages or calls to new or changed devices until you accept them in Member Security. Turn this on if you want to catch unrecognized devices.',
      deviceTrustPauseEnabled: 'Sending and calling will pause until you accept new devices.',
      deviceTrustPauseDisabled: 'You can keep sending. New or changed devices will show a warning you can dismiss.',
      spacesTitle: 'Spaces',
      spacesDescription: 'Control who can send you a direct invitation to a Space. Invite links you choose to open are not affected.',
      spaceInvitesFriendsTitle: 'Allow Space invites from friends',
      spaceInvitesFriendsHint: 'People you are friends with can send you a direct Space invite.',
      spaceInvitesMembersTitle: 'Allow Space invites from people in my Spaces',
      spaceInvitesMembersHint: 'People who share an active Space with you can send you a direct Space invite, even if you are not friends.',
      spaceInvitesAnyoneTitle: 'Allow Space invites from anyone',
      spaceInvitesAnyoneHint: 'Anyone who can invite to a Space can send you a direct invite, including people who are not friends and do not share a Space with you.',
      spaceInvitesUpdated: 'Space invite settings saved.',
      spaceInvitesError: 'Failed to update setting. Please try again.',
      autoMatchCiphersTitle: 'Automatically match Ciphers against encrypted channels',
      autoMatchCiphersHint:
        'When you open an encrypted channel, Adieuu checks your Ciphers for a match. Turn this off to use the manual check instead.',
      autoMatchCiphersEnabled: 'Encrypted channels will be matched against your Ciphers automatically.',
      autoMatchCiphersDisabled: 'Encrypted channels will wait until you check your Ciphers.',
      messageSearchTitle: 'Local message search',
      messageSearchDescription:
        'End-to-end encrypted chats search on your device. Plaintext may be cached locally for speed; choose defaults that match your threat model.',
      messageSearchRetentionLabel: 'After search, keep plaintext index',
      messageSearchRetentionWipeNow: 'Delete when I close search',
      messageSearchRetentionNever: 'Keep until I clear it',
      messageSearchRetentionAfter1h: 'Delete after 1 hour',
      messageSearchRetentionAfter1d: 'Delete after 1 day',
      messageSearchRetentionAfter7d: 'Delete after 7 days',
      messageSearchRetentionAfter30d: 'Delete after 30 days',
      messageSearchModeLabel: 'Build search index',
      messageSearchModeOnDemand: 'When I open search (uses less storage)',
      messageSearchModeWarm: 'As I read messages (faster search, more plaintext on device)',
      changePassword: {
        title: 'Change Alias Password',
        description: 'Passwords are used both to lookup and authenticate into your Aliases. Every Alias must have a different password. If you need to change the password for your Alias, you can do so below. Just enter your existing password for the alias you want to change, and the desired new password. Your encrypted key bundle will be re-encrypted with the new password.',
        currentPassword: 'Current password',
        newPassword: 'New password',
        confirmPassword: 'Confirm new password',
        currentPassphrase: 'Current alias password',
        newPassphrase: 'New alias password',
        confirmPassphrase: 'Confirm new alias password',
        submit: 'Change Alias Password',
        processing: 'Changing alias password...',
        success: 'Alias password changed successfully.',
        errorMismatch: 'New passwords do not match.',
        errorSamePassphrase: 'New password must be different from current password.',
        errorSamePassword: 'New password must be different from current password.',
        errorMinLength: 'Password must be at least 8 characters.',
        errorDecryptFailed: 'Failed to decrypt your key bundle. Is your current password correct?',
        errorFailed: 'Password change failed. Please try again.',
        errorPassphraseExists: 'An alias with this password already exists. Each alias needs its own password.',
        errorBundleNotFound: 'Could not find the encrypted keys for this alias. Please try again.',
        errorInvalid: 'Current password is incorrect.',
        localRewrapFailed:
          'Your password was changed, but updating locally stored keys failed. Some message history on this device may be unavailable until you sign in again.',
        localRewrapAmbiguous:
          'Your password was changed, but local keys could not be updated automatically because more than one alias on this device uses the same password. Sign in to each alias to restore its history.',
        securityNote: 'Choose a strong password and store it safely. Without it, encrypted data for this alias cannot be recovered.',
        errorSession:
          'Your account session is not available. Please sign in to your account and try again.',
        identityOnlyTitle: 'Account sign-in required',
        identityOnlyBody:
          'Changing your alias password requires a full account sign-in. It is not available when you are signed in to an alias.',
        identityOnlyHint:
          'Sign out of your alias, sign in to your account, then go to Account → Security.',
        whyAccountSignInLink: 'Why is this necessary?',
        whyAccountSignInTitle: 'Why account sign-in is required',
        whyAccountSignInP1:
          'Your account and your aliases use separate cryptographic keys on purpose: a problem with one should not automatically expose the other. Unfortunately, this means you can\'t be signed into both (we originally supported this but felt the risks of your account and alias being linked together was too high if/when our systems were breached).',
        whyAccountSignInP2:
          'One of the many protections we employ for your Aliases, however, includes deriving some of your Alias\' encrypted key material from data that only exists in an Account. This makes it much harder for an attacker to guess an Alias password, even if they managed to breach our database.',
        whyAccountSignInP3:
          'An Alias session does not include those account-held secrets, so the service cannot complete the password change until you sign into your Account.',
      },
    },
    // Custom emoji management
    customEmojis: {
      title: 'Custom Emojis',
      subtitle: 'Upload and manage custom emojis you can use in conversations.',
      usage: '{{used}} of {{limit}} slots used',
      add: 'Add Emojis',
      limitReached: 'You have reached your custom emoji limit. Upgrade your subscription for more.',
      empty: 'You haven\'t added any custom emojis yet.',
      createTitle: 'Add Custom Emojis',
      selectFile: 'Select image',
      selectFiles: 'Select images',
      fileHint: 'PNG, WebP, or GIF. Max 256 KB each.',
      dropHint: 'Drag and drop or click to browse',
      addMore: 'Add more',
      changeFile: 'Change file',
      shortcodeLabel: 'Shortcode',
      nameLabel: 'Display name',
      save: 'Save {{count}} Emoji',
      save_one: 'Save {{count}} Emoji',
      save_other: 'Save {{count}} Emojis',
      saving: 'Saving...',
      deleteTitle: 'Delete custom emoji?',
      deleteDescription: 'This emoji will be permanently deleted. Messages that already contain it will show the shortcode text instead.',
      done: 'Done',
      uploadMore: 'Upload More',
      sortLabel: 'Sort by',
      sortName: 'Name',
      sortShortcode: 'Shortcode',
      sortRecent: 'Recently uploaded',
    },
    // Audio & Video page (device-scoped, localStorage)
    audioVideo: {
      title: 'Audio & Video',
      subtitle: 'Choose the microphone, camera, and speakers used when you join calls and Audio/Video channels, and set input/output levels and transmit quality.',
      soundsCta:
        "Looking for message, call, and other sounds? Go to <notificationsLink>Notifications</notificationsLink>",
      notLoggedIn: 'Sign in to an alias to manage Audio & Video settings.',
      sections: {
        input: 'Microphone',
        output: 'Speakers',
        camera: 'Camera',
        screenshare: 'Screen share',
        advanced: 'More options',
      },
      refresh: 'Refresh devices',
      permissionHint: 'Allow microphone and camera access to see device names.',
      permissionDenied: 'Permission denied. Allow access in your browser settings and try again.',
      systemDefault: 'System default',
      noDevices: 'No devices found',
      microphoneLabel: 'Microphone',
      cameraLabel: 'Camera',
      speakerLabel: 'Speakers',
      speakerUnsupported: 'Your browser does not support choosing an output device; the system default is used.',
      inputTitle: 'Microphone',
      inputDescription: 'Choose your microphone, set transmit quality and input level, and test that it is picking up sound.',
      inputVolumeLabel: 'Input volume',
      inputVolumeHint: 'Adjusts your microphone gain (0–200%).',
      outputTitle: 'Speakers',
      outputDescription: 'Choose your speakers, set how loud other participants sound, and play a test tone.',
      outputVolumeLabel: 'Output volume',
      outputVolumeHint: 'Adjusts how loud other participants sound (0–200%).',
      cameraTitle: 'Camera',
      cameraDescription: 'Choose your camera and check how you look before joining a call.',
      cameraPreviewLabel: 'Live preview',
      cameraPreviewIdle: 'Click “Preview camera” to see your video.',
      cameraPreviewStart: 'Preview camera',
      cameraPreviewStop: 'Stop preview',
      cameraPreviewError: 'Could not start the camera preview. Check permissions and try again.',
      micQualityLabel: 'Microphone quality',
      screenshareTitle: 'Screen share',
      screenshareDescription:
        'Video and audio options specifically for your screenshares.',
      screenshareQualityLabel: 'Audio Quality',
      screenshareVideoLabel: 'Video Quality',
      screenshareVideoStandard: 'Standard (30 fps)',
      screenshareVideoOriginal: 'Original detail (30 fps)',
      screenshareVideoStandardHint:
        'Scales the shared screen down to your plan’s resolution limit at 30 fps. Lightest on CPU and upload.',
      screenshareVideoOriginalHint:
        'Keeps the shared screen at its own resolution, up to your plan’s limit, and spends extra bitrate so text stays sharp. Heavier on CPU and upload.',
      qualityLow: 'Low (24kbps)',
      qualityStandard: 'Standard (48kbps)',
      qualityHigh: 'High (96kbps)',
      qualityUltra: 'Ultra (128kbps)',
      qualityVinylSnob: 'Vinyl Snob (192kbps)',
      qualityMolecularEuphoria: 'Molecular Euphoria (320kbps)',
      qualityLowHint: "24 kbps, mono. Best for performance or weak connections. Might give you flashbacks to online calls in 2002.",
      qualityStandardHint: '48 kbps, mono. Lower CPU and bandwidth; still clear speech.',
      qualityHighHint: "96 kbps, mono. If you're just speaking into a normal mic, this is typically the best balance between performance and clarity.",
      qualityUltraHintMic:
        '128 kbps. For stereo interfaces or playing music into the mic. Will not usually make your voice clearer in a typical mic, but does affect system performance.',
      qualityUltraHintScreenshare:
        '128 kbps. Stereo mix from the shared tab or desktop (games, apps, system audio). If you have performance issues while scrensharing, try reducing this setting.',
      qualityVinylSnobHintMic:
        'Infusing your stream with 192kbps of organic, non-GMO analog warmth and the subtle scent of patchouli-scented record sleeves.',
      qualityVinylSnobHintScreenshare:
        '192 kbps. Richer stereo mix from the shared tab or desktop. Uses more CPU and bandwidth than Ultra.',
      qualityMolecularEuphoriaHintMic:
        "At 320 kbps stereo, you aren't just hearing the acoustic guitar; you can hear the moisture evaporating from the wood. Near-transparent stereo for interfaces or playing music into the mic. Highest CPU and bandwidth of these presets.",
      qualityMolecularEuphoriaHintScreenshare:
        '320 kbps. Near-transparent stereo mix from the shared tab or desktop. Highest CPU and bandwidth of these presets.',
      qualityLockedHint: 'Requires an Insider subscription or Founder entitlement.',
      joinMicOff: 'Default to microphone off when joining a call',
      joinMicOffHint: 'Start muted in conversation calls and Space Audio/Video channels. You can unmute anytime.',
      joinCameraOff: 'Default to camera off when joining a call',
      joinCameraOffHint: 'Join with your camera off. You can turn it on anytime during the call.',
      joinVoiceOnChannelClick: 'Join the call when clicking an Audio/Video channel',
      joinVoiceOnChannelClickHint:
        'Clicking a channel opens its text chat and joins the call. Turn this off to open the text chat only, and join from the channel header.',
      showDeviceSetup: 'Show device selections before joining a call',
      showDeviceSetupHint:
        'Ask which microphone, speakers, and camera to use before starting or joining a conversation call or Space Audio/Video channel.',
      testMicStart: 'Test microphone',
      testMicStop: 'Stop test',
      testMicLevelLabel: 'Input level',
      testSpeaker: 'Play test sound',
      advancedTitle: 'More options',
      advancedDescription: 'Join behaviour and additional audio processing options.',
      noiseSuppression: 'Noise suppression',
      noiseSuppressionHint: 'Coming soon.',
      comingSoon: 'Coming soon',
    },
    // Appearance page (alias-scoped)
    appearance: {
      title: 'Alias Appearance',
      subtitle: 'Customise the look and feel for your alias "{{alias}}".',
      overrideTitle: 'Theme Override',
      overrideDescription:
        'Enable a distinct theme for this alias so you can tell at a glance which alias is active.',
      enableOverride: 'Enable Override',
      disableOverride: 'Disable Override',
      overrideEnabled: 'Alias theme override enabled.',
      overrideDisabled: 'Alias theme override removed. Inheriting account settings.',
      inheriting: 'This alias is inheriting <accountLink>appearance settings from your account</accountLink>.',
      themeApplied: 'Theme applied to this alias.',
      themeSaved: 'Custom theme saved and applied to this alias.',
      themeDeleted: 'Custom theme deleted.',
      importFailed: 'Failed to import theme',
      importSuccess: 'Theme imported and applied to this alias.',
      messageDisplayTitle: 'Message Display',
      messageDisplayDescription: 'Control how messages are displayed in your conversations.',
      showArtifactsTitle: 'Show Message Artifacts',
      showArtifactsHint: 'When enabled, deleted messages, expired forward secrecy messages, and messages that could not be decrypted are shown in conversations. When disabled, these artifacts are hidden for a cleaner view. This is a local display preference only and does not affect message storage.',
      reactionNotificationsTitle: 'Reaction Notifications',
      reactionNotificationsHint: 'When enabled, you will receive notifications and unread indicators when someone reacts to your messages. This only applies to reactions on messages you sent.',
      embedVisibilityTitle: 'Link Embeds',
      embedVisibilityHint: 'Control whether link previews and video embeds are shown in messages.',
      embedNone: 'None',
      embedAll: 'All',
      embedAllowlist: 'Allowlist',
      embedAllowlistPlaceholder: 'e.g. youtube.com',
      embedMaxWidthTitle: 'Max Embed Width',
      embedMaxWidthNone: 'No max',
      embedToggleHide: 'Click to hide embed',
      embedToggleDisabled: 'Embed available but hidden (embeds disabled)',
      embedToggleNotAllowlisted: 'Embed available but hidden (domain not in allowlist)',
      sidebarTitle: 'Sidebar',
      sidebarDescription: 'Choose where the button that collapses the main sidebar sits.',
      sidebarTogglePositionTitle: 'Collapse button position',
      sidebarTogglePositionHint: 'Top, center, or bottom of the window. Center is the default.',
      sidebarToggleTop: 'Top',
      sidebarToggleCenter: 'Center',
      sidebarToggleBottom: 'Bottom',
      sidebarToggleOffsetTitle: 'Collapse button offset',
      sidebarToggleOffsetHint: 'Pixels added to the button’s top edge. Positive moves it down, negative moves it up.',
      sidebarToggleOffsetUnit: 'px',
    },
} as const;

export const badges = {
  vanguard: 'Vanguard',
  founder: 'Founder',
  top100: '100',
  top1000: '1000',
  overachiever: 'Overachiever',
  vanguardCriteria: 'Adieuu Vanguard supporter!',
  founderCriteria: 'Adieuu Founder supporter!',
  top100Criteria: 'One of the first 100 users on Adieuu',
  top1000Criteria: 'One of the first 1000 users on Adieuu!',
  overachieverCriteria: 'Awarded for earning every achievement.',
  notEarned: 'You haven\'t earned this badge yet.',
} as const;
